import { InMemoryCache } from '@apollo/client/cache';
import { ApolloClient } from '@apollo/client';
//import { loader } from 'graphql.macro';
import { ApolloLink, from, Operation } from '@apollo/client';
import DebounceLink from 'apollo-link-debounce';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createHttpLink } from '@apollo/client/link/http';
import { RetryLink } from '@apollo/client/link/retry';

import { LocalStorageKey, getItem, removeItem } from 'services/localStorageService';
import errorHandler from './errors';
import moment, { Moment } from 'moment';
import { TokenRefreshLink } from 'apollo-link-token-refresh';
import authService from 'services/cognito/lib';
import { jwtDecode } from 'jwt-decode';
import { version } from "../projectVersion";

const DEFAULT_DEBOUNCE_TIMEOUT = 200;

const debounceLink = new DebounceLink(DEFAULT_DEBOUNCE_TIMEOUT);

const retryLink = new RetryLink({
    delay: {
        initial: 15000,
        max: Infinity,
        jitter: false,
    },
    attempts: {
        max: Infinity,
        retryIf: (error, _operation) => {
            return (_operation.operationName === 'listOrders' || _operation.operationName === 'listOffers') && !!error;
        },
    },
});

const httpLink = createHttpLink({
    uri: window.config.API_GRAPHQL_ENDPOINT,
});

const authLink = setContext((_, { headers }) => {
    // get the authentication token from local storage if it exists
    const token = getItem<string>(LocalStorageKey.ACCESS_TOKEN);

    // return the headers to the context so httpLink can read them
    return {
        headers: {
            ...headers,
            authorization: token ? `Bearer ${token}` : '',
            "x-app-name": "FOODI-ORDERS",
            "x-app-channel": "WEB",
            "x-app-version": version
        },
    };
});

const omitTypenameLink = new ApolloLink((operation: Operation, forward: any) => {
    if (operation.variables) {
        const omitTypename = (key: string, value: any) => (key === '__typename' ? undefined : value);
        operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename);
    }
    return forward(operation);
});

const cache = new InMemoryCache();
/*
cache.writeData({
    data: {
        isOffline: false,
    },
});
 */

const TOKEN_EXPIRATION_OFFSET = 5;
const getExpirationTokenInfoFromExp = (
    exp: number
): {
    emissionDate: string;
    expirationDate: string;
    expireIn: number;
} => {
    const momentNowDate: Moment = moment();
    const momentExpirationDate: Moment = moment(exp * 1000);
    const expireIn: number = momentExpirationDate.diff(momentNowDate, 's');
    const emissionDate: string = momentNowDate.toISOString();
    const expirationDate: string = momentExpirationDate.subtract(TOKEN_EXPIRATION_OFFSET, 's').toISOString();

    return { emissionDate, expirationDate, expireIn };
};

const updateAccessToken = (accessToken: string) => {
    const decodedAccessToken = jwtDecode(accessToken);
    const { exp } = decodedAccessToken as { exp: number };
    const { expirationDate } = getExpirationTokenInfoFromExp(exp);
    authService.setSession({
        accessToken,
        expiration: expirationDate,
    });
};

const promiseResponse = (response): Promise<boolean> =>
    new Promise((resolve) => {
        resolve(!!response);
    });

export const refreshLink = new TokenRefreshLink({
    accessTokenField: 'accessToken',
    isTokenValidOrUndefined: (): Promise<boolean> => {
        const expirationDate = getItem(LocalStorageKey.TOKEN_EXPIRATION_DATE);
        if (!expirationDate) {
            return promiseResponse(!expirationDate);
        }
        const tokenIsValid = moment(expirationDate).isAfter(moment().toISOString());
        return promiseResponse(tokenIsValid);
    },
    fetchAccessToken: async (): Promise<Response> => {
        try {
            const results = await authService.refreshSession();
            const response = new Response(JSON.stringify({ accessToken: results?.token }), {
                status: 200,
                statusText: 'OK',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            return response;
        } catch (error) {
            throw error;
        }
    },
    handleError: (_error) => {
        authService.logout({
            cleanSessionInfoCallback: () => {
                removeItem(LocalStorageKey.ACCESS_TOKEN);
            },
        });
    },
    handleFetch: updateAccessToken,
    handleResponse: () => async (result) => {
        return await result.json();
    },
});

export const client = new ApolloClient({
    link: from([
        refreshLink,
        new ApolloLink((operation, forward) => {
            // todo - writ result of network status query in cache
            /*client.readQuery({
                query: getNetworkStatus,
            });*/
            /*const networkStatus = client.readQuery({ query: getNetworkStatus });
            if (networkStatus.isOffline) {
                client.writeData({ data: { isOffline: false } });
            }*/
            return forward!(operation);
        }),
        debounceLink,
        retryLink,
        onError((error) => {
            errorHandler.onError(error, client);
        }),
        omitTypenameLink,
        authLink,
        httpLink,
    ]),
    cache,
});
